import { SubformsInputBlock as SubformsInputBlockType } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { View as RNView, StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextSubtitle } from '@shared/components/typography';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isAuditTagComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { MBAlert } from '@shared/util/alert';
import { i18n } from '@shared/util/i18n';
import { BaseSubformRoute, useNavStackContext, useNavigator } from '@shared/util/navigation';
import { CardChannelContext } from '../../contexts/channel';
import { CardFormContext } from '../../contexts/form';
import { MBBlockLabel } from '../../utils/label';
import { SubformContent } from './SubformsContent';
import { SubformsSheet } from './sheet/SubformsSheet';

interface Props {
  block: SubformsInputBlockType;
  value: any;
}

export function SubformsInputBlock({ block, value }: Props) {
  const { setFieldValue } = useContext(CardFormContext);
  const channel = useContext(CardChannelContext);
  const colors = useContext(ColorThemeContext);
  const [sheetOpen, setSheetOpen] = useState(false);
  const containerRef = useRef<RNView>(null);
  const { workflowTemplate } = useWorkflowTemplate(channel?.workflowTemplateId);
  const hasSubform = !isNil(value);
  const nav = useNavStackContext<BaseSubformRoute>();
  const navigator = useNavigator();
  const channelSlug = channel?.slug;
  const anchorName = `--subforms-input-block-${block.name}`;

  const onPressContainer = useCallback(() => {
    setSheetOpen(true);
  }, []);

  const onSelectSubform = useCallback(
    (subformWorkflowTemplate: MBAPI.WorkflowTemplate) => {
      const componentsWithDefaults =
        subformWorkflowTemplate.components
          ?.filter(isAuditTagComponent)
          .filter(c => !isEmpty(c.default?.value)) ?? [];

      const defaultData = componentsWithDefaults.reduce((acc, component) => {
        acc[component.id] = { tags: component.default?.value ?? [] };
        return acc;
      }, {});

      setFieldValue(block.name, {
        workflowTemplateId: subformWorkflowTemplate.id,
        data: defaultData,
      });
      setSheetOpen(false);
    },
    [block.name, setFieldValue],
  );

  const onSelect = useCallback(
    (workflowTemplateId: string) => {
      nav.push({
        name: 'subform',
        params: {
          workflowTemplateId,
          parentComponentId: block.name,
        },
      });
      setSheetOpen(false);
    },
    [block.name, nav],
  );

  const onClear = useCallback(() => {
    MBAlert.confirm({
      message: i18n('card.blocks.subformsInput.confirmClear'),
      confirmText: i18n('common.ok'),
      onConfirm: () => {
        setFieldValue(block.name, null);
      },
    });
  }, [block.name, setFieldValue]);

  const navigateToAdd = useCallback(() => {
    if (isNil(channelSlug)) {
      return;
    }
    navigator.navigate('SubformsList', { channelSlug: channelSlug! });
  }, [channelSlug, navigator]);

  return (
    <MBTouchableOpacity onPress={hasSubform ? undefined : onPressContainer}>
      <View style={{ anchorName }}>
        {block.label && (
          <View paddingLeft={3} paddingVertical={1}>
            <MBBlockLabel label={block.label} />
          </View>
        )}
        {hasSubform ? (
          <RNView
            ref={containerRef}
            style={{ paddingHorizontal: grid(1), alignContent: 'center', alignItems: 'center' }}
          >
            <SubformContent subform={value} onSelect={onSelect} onClear={onClear} />
          </RNView>
        ) : (
          <View paddingHorizontal={1}>
            <View style={{ anchorName }}>
              <MBView
                paddingVertical={2}
                center
                style={[
                  styles.container,
                  {
                    backgroundColor: colors.backgroundInactive,
                    borderColor: colors.input.borderInactive,
                  },
                ]}
              >
                <View row centerVertically>
                  <>
                    <MBIconV2
                      color={colors.foregroundActive}
                      size={grid(5)}
                      name={block.icon || 'list'}
                    />
                    <View paddingLeft={1} />
                    <MBTextSubtitle color={colors.foregroundActive}>
                      {block.placeholder}
                    </MBTextSubtitle>
                  </>
                </View>
              </MBView>
            </View>
          </View>
        )}
      </View>
      {sheetOpen && !isNil(channelSlug) && (
        <SubformsSheet
          anchorName={anchorName}
          blockLabel={block.label ?? ''}
          workflowTemplate={workflowTemplate}
          onClose={() => setSheetOpen(false)}
          onSelect={onSelectSubform}
          navigateToAdd={navigateToAdd}
        />
      )}
    </MBTouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: grid(1),
    borderStyle: 'dashed',
    borderWidth: 2,
  },
});
